import { Box, Button, Paper, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

// Vertical "timeline" layout for short streaks (3 & 7 days).

const ShortStreak = ({ days = [], currentDay, claimed, onClaim }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mt: 1 }}>
      {days.map((dayData, idx) => {
        const { day, status, reward, isSpecial } = dayData;
        const isLast = idx === days.length - 1;
        const noMultiplier =
          dayData?.rewardType == "GIFTCARD" ||
          dayData?.rewardType == "BOOSTER" ||
          dayData?.rewardType == "FREE_SPIN";

        return (
          <Box key={day} sx={{ display: "flex", gap: 2 }}>
            {/* Timeline rail */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: "15px",
                  color: "#fff",
                  background:
                    status === "claimed"
                      ? "linear-gradient(135deg, #4caf50, #2e7d32)"
                      : status === "available"
                      ? `linear-gradient(135deg, ${reward.color}, ${reward.color}90)`
                      : "rgba(255,255,255,0.12)",
                  border:
                    status === "available"
                      ? "2px solid #fff"
                      : "2px solid rgba(255,255,255,0.3)",
                }}
              >
                {status === "claimed" ? "✓" : day}
              </Box>
              {!isLast && (
                <Box
                  sx={{
                    width: "3px",
                    flex: 1,
                    minHeight: 28,
                    my: 0.5,
                    borderRadius: 2,
                    background:
                      status === "claimed"
                        ? "linear-gradient(#4caf50, rgba(255,255,255,0.2))"
                        : "rgba(255,255,255,0.2)",
                  }}
                />
              )}
            </Box>

            {/* Card */}
            <Paper
              sx={{
                flex: 1,
                mb: 2,
                p: { xs: 1.75, sm: 2.25 },
                borderRadius: 3,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                position: "relative",
                background:
                  status === "claimed"
                    ? "linear-gradient(135deg, rgba(76,175,80,0.85), rgba(46,125,50,0.85))"
                    : status === "available"
                    ? `linear-gradient(135deg, ${reward.color}, ${reward.color}90)`
                    : "rgba(0,0,0,0.35)",
                border: isSpecial
                  ? "2px solid #ffeb3b"
                  : "1px solid rgba(255,255,255,0.15)",
                boxShadow:
                  status === "available"
                    ? "0 16px 36px -18px rgba(0,0,0,0.8)"
                    : "none",
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: status === "available" ? "translateX(4px)" : "none",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
                <Typography sx={{ fontSize: { xs: "34px", sm: "42px" }, lineHeight: 1 }}>
                  {reward.icon}
                </Typography>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "13px", opacity: 0.9 }}>
                      {t('streak.shortStreakDay', { day })}
                    </Typography>
                    {isSpecial && (
                      <Box
                        sx={{
                          background: "linear-gradient(45deg, #ffeb3b, #ffc107)",
                          color: "#000",
                          px: 1,
                          py: 0.1,
                          borderRadius: 5,
                          fontSize: "10px",
                          fontWeight: 800,
                          textTransform: "uppercase",
                        }}
                      >
                        {dayData?.occasion || t('streak.shortStreakSpecial')}
                      </Box>
                    )}
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: "15px", sm: "17px" } }}>
                    {reward.text}{" "}
                    {noMultiplier
                      ? ""
                      : t('streak.shortStreakMultiplier', {
                          multiplier: dayData?.multiplier || 1,
                        })}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ flexShrink: 0 }}>
                {status === "available" && (
                  <Button
                    disabled={claimed && day === currentDay}
                    sx={{
                      px: 2.5,
                      py: 0.8,
                      borderRadius: 6,
                      fontWeight: 800,
                      fontSize: "13px",
                      color: claimed && day === currentDay ? "#fff" : "#000",
                      background: claimed && day === currentDay
                        ? "linear-gradient(135deg, #4caf50, #2e7d32)"
                        : "#fff",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        background: claimed && day === currentDay
                          ? "linear-gradient(135deg, #4caf50, #2e7d32)"
                          : "#f1f1f1"
                      },
                      "&.Mui-disabled": {
                        color: "#fff",
                        background: "linear-gradient(135deg, #4caf50, #2e7d32)",
                      }
                    }}
                    onClick={() => onClaim(dayData)}
                  >
                    {claimed && day == currentDay
                      ? t('streak.shortStreakClaimed')
                      : dayData?.rewardType === "BOOSTER"
                      ? t('streak.shortStreakBuyNow')
                      : t('streak.shortStreakClaimNow')}
                  </Button>
                )}
                {status === "claimed" && (
                  <Typography sx={{ fontWeight: 700, fontSize: "13px", opacity: 0.95 }}>
                    {t('streak.shortStreakClaimedCheck')}
                  </Typography>
                )}
                {status === "locked" && (
                  <Typography sx={{ fontWeight: 700, fontSize: "13px", opacity: 0.7 }}>
                    🔒
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        );
      })}
    </Box>
  );
};

export default ShortStreak;
